import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import isEmpty from 'lodash/isEmpty';
import type { IMember } from '@/Interfaces/Data';
import { ROLE_TYPES } from '@/Interfaces/Data';
import { cpfLabel } from '@/Utils/FnUtils';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import type { TypePostMemberForm } from './MemberRegistrationForm';
import MemberRegistrationForm from './MemberRegistrationForm';
import {
  getMember,
  selectMemberSelected,
  setRegistrationMemberRequest,
} from '@/_store/modules/members';

const MEMBER_NOT_FOUND_TOAST = {
  message: 'Membro não encontrado',
  toastId: 'member-not-found',
};

const INITIAL_VALUES: TypePostMemberForm = {
  name: '',
  cpf: '',
  phone: '',
  email: '',
  isEdit: false,
  password: '',
  type: ROLE_TYPES.ADMIN,
  city: [],
  status: false,
  permissions: {
    delivery: {
      all: false,
      associateDelivery: false,
      deliveryStatus: false,
      reopenDelivery: false,
      cancelDelivery: false,
      events: false,
    },
    deliveryman: {
      all: false,
      createDeliveryman: false,
      editDeliveryman: false,
      approvePreRegister: false,
      blockDeliveryman: false,
      dedicateDeliveryman: false,
    },
    place: {
      all: false,
      editPlace: false,
      credit: false,
      debit: false,
      createAndEditFlare: false,
      dedicateOrder: false,
    },
    city: {
      all: false,
      editFare: false,
    },
  },
};

function MemberRegistration() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { memberId } = useParams<{ memberId: string }>();

  const [onSubmit, setOnSubmit] = useState(false);
  const [values, setValues] = useState<TypePostMemberForm | null>(null);

  const fetching = useAppSelector(state => state.members.fetching);
  const requestErrors = useAppSelector(state => state.members.errors);
  const memberSelected = useAppSelector(selectMemberSelected);

  useEffect(() => {
    if (memberId) {
      dispatch(getMember({ id: memberId }));
      return;
    }
    setValues(INITIAL_VALUES);
  }, [memberId]);

  useEffect(() => {
    if (!memberId || onSubmit || fetching || memberSelected) return;

    if (!isEmpty(requestErrors)) {
      toast.warn(MEMBER_NOT_FOUND_TOAST.message, {
        toastId: MEMBER_NOT_FOUND_TOAST.toastId,
      });
      navigate('/equipe', { replace: true });
    }
  }, [memberId, onSubmit, fetching, memberSelected, requestErrors]);

  const getCitiesFromMember = (member: IMember): string[] => {
    const cities: string[] = [];
    if (member.municipioIds) {
      member.municipioIds.forEach(e => cities.push(e._id));
    }
    return cities;
  };

  useEffect(() => {
    if (!values && memberSelected) {
      setValues({
        name: memberSelected.nome,
        cpf: cpfLabel(memberSelected.cpf),
        phone: memberSelected.celular,
        email: memberSelected.email,
        isEdit: !!memberId,
        password: '',
        type: memberSelected.managerLogType,
        city: getCitiesFromMember(memberSelected),
        status: memberSelected.isAtivo,
        permissions: {
          delivery: {
            all: !!memberSelected?.permissions?.delivery.all,
            associateDelivery:
              !!memberSelected?.permissions?.delivery.associateDelivery,
            deliveryStatus:
              !!memberSelected?.permissions?.delivery.deliveryStatus,
            reopenDelivery:
              !!memberSelected?.permissions?.delivery.reopenDelivery,
            cancelDelivery:
              !!memberSelected?.permissions?.delivery.cancelDelivery,
            events: !!memberSelected?.permissions?.delivery.events,
          },
          deliveryman: {
            all: !!memberSelected?.permissions?.deliveryman.all,
            createDeliveryman:
              !!memberSelected?.permissions?.deliveryman.createDeliveryman,
            editDeliveryman:
              !!memberSelected?.permissions?.deliveryman.editDeliveryman,
            approvePreRegister:
              !!memberSelected?.permissions?.deliveryman.approvePreRegister,
            blockDeliveryman:
              !!memberSelected?.permissions?.deliveryman.blockDeliveryman,
            dedicateDeliveryman:
              !!memberSelected?.permissions?.deliveryman.dedicateDeliveryman,
          },
          place: {
            all: !!memberSelected?.permissions?.place.all,
            editPlace: !!memberSelected?.permissions?.place.editPlace,
            credit: !!memberSelected?.permissions?.place.credit,
            debit: !!memberSelected?.permissions?.place.debit,
            createAndEditFlare:
              !!memberSelected?.permissions?.place.createAndEditFlare,
            dedicateOrder: !!memberSelected?.permissions?.place.dedicateOrder,
          },
          city: {
            all: !!memberSelected?.permissions?.city?.all,
            editFare: !!memberSelected?.permissions?.city?.editFare,
          },
        },
      });
    }
  }, [memberSelected]);

  const checkRequestErrors = useCallback(() => {
    if (requestErrors.length === 0) return navigate('/equipe');
    setOnSubmit(false);
  }, [requestErrors]);

  useEffect(() => {
    if (onSubmit && !fetching) checkRequestErrors();
  }, [onSubmit, fetching]);

  const submit = (data: TypePostMemberForm) => {
    const payload = {
      cpf: data.cpf,
      email: data.email,
      nome: data.name,
      senha: data.password || undefined,
      celular: data.phone,
      municipioIds: data.city || [],
      managerLogType: data.type as ROLE_TYPES,
      isAtivo: data.status,
      permissions: data.permissions,
    };
    dispatch(
      setRegistrationMemberRequest({
        payload,
        id: memberId,
      }),
    );
    setOnSubmit(true);
  };

  return (
    <div className="container">
      {values && (
        <MemberRegistrationForm
          values={values}
          onSubmit={submit}
          loading={fetching}
        />
      )}
    </div>
  );
}

export default MemberRegistration;
